import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Home } from "lucide-react";
import { usePageMeta } from "@/hooks/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentResult = ({ success = false }) => {
  usePageMeta(success ? "Vagus Digital | Payment complete" : "Vagus Digital | Payment cancelled");
  const location = useLocation();
  const sessionId = new URLSearchParams(location.search).get("session_id");
  const [state, setState] = useState(success ? "checking" : "cancelled");
  const tries = useRef(0);

  useEffect(() => {
    if (!success || !sessionId) return;
    let timer;
    const poll = async () => {
      tries.current += 1;
      try {
        const res = await fetch(`${API}/payments/status/${sessionId}`);
        if (res.ok) {
          const data = await res.json();
          if (data.payment_status === "paid") {
            setState("paid");
            return;
          }
        }
      } catch {
        /* keep polling */
      }
      if (tries.current < 24) timer = setTimeout(poll, 2500);
      else setState("timeout");
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, success]);

  return (
    <section className="flex min-h-screen items-center justify-center bg-paper px-6 py-24">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md rounded-2xl bg-white p-10 text-center shadow-ambient"
        data-testid={`payment-${state}`}
      >
        {state === "paid" && (
          <>
            <CheckCircle2 className="mx-auto h-10 w-10 text-jade" strokeWidth={1.75} />
            <h1 className="mt-5 font-display text-3xl font-light tracking-tight text-ink">
              Payment received.
            </h1>
            <p className="mt-4 leading-relaxed text-ink2">
              Thank you, that's cleared and your receipt is on its way from
              Stripe. I'll be in touch about next steps.
            </p>
          </>
        )}
        {state === "checking" && (
          <>
            <h1 className="font-display text-3xl font-light tracking-tight text-ink">
              Checking your payment…
            </h1>
            <p className="mt-4 leading-relaxed text-ink2">
              Confirming with Stripe. This only takes a moment. You can leave
              this page; your receipt will still arrive.
            </p>
          </>
        )}
        {state === "timeout" && (
          <>
            <h1 className="font-display text-3xl font-light tracking-tight text-ink">
              Almost there.
            </h1>
            <p className="mt-4 leading-relaxed text-ink2">
              Your payment is still confirming with Stripe. If nothing arrives
              within an hour, email groupvagus@gmail.com and I'll sort it.
            </p>
          </>
        )}
        {state === "cancelled" && (
          <>
            <XCircle className="mx-auto h-10 w-10 text-ink2/50" strokeWidth={1.75} />
            <h1 className="mt-5 font-display text-3xl font-light tracking-tight text-ink">
              Payment cancelled.
            </h1>
            <p className="mt-4 leading-relaxed text-ink2">
              No money has moved. If that was a mistake, you can start again
              whenever you're ready.
            </p>
            <Link
              to="/pay"
              data-testid="payment-retry-link"
              className="mt-8 inline-flex items-center gap-2 rounded-full border border-jade/30 px-7 py-3 text-sm font-medium text-jade transition-colors duration-300 hover:bg-jade/5"
            >
              Try again
            </Link>
          </>
        )}
        <Link
          to="/"
          data-testid="payment-home-link"
          className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-jade underline-offset-4 hover:underline"
        >
          <Home className="h-4 w-4" /> Back to site
        </Link>
      </motion.div>
    </section>
  );
};

export default PaymentResult;